import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const TAGS = ["first", "type", "when", "phone"];
const SAMPLE = { first: "Michael", when: "Thu Nov 6 at 2:30 PM" };
const BLANK = { name: "", template: "Hi {first}, ... Reply STOP to opt out.", enabled: false, timing: "before", days: 1, appt_types: [], status: "any" };
const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";

const preview = (f, phone) => f.template.replace(/\{(\w+)\}/g, (m, k) => (k === "type" ? f.appt_types[0] || "Consultation" : k === "phone" ? phone : SAMPLE[k] ?? m)).replace(/[ \t]{2,}/g, " ").trim();

const TypePicker = ({ f, set }) => {
  const { data: types = [] } = useQuery({ queryKey: ["custom-text-types"], queryFn: async () => (await api.get("/custom-texts/appointment-types")).data });
  const [find, setFind] = useState("");
  const shown = types.filter((t) => f.appt_types.includes(t) || t.toLowerCase().includes(find.trim().toLowerCase()));
  const toggle = (t) => set({ appt_types: f.appt_types.includes(t) ? f.appt_types.filter((x) => x !== t) : [...f.appt_types, t] });
  return (
    <div>
      <p className="text-xs font-medium text-slate-700">Who gets it</p>
      <p className="text-[11px] text-slate-500">{f.appt_types.length ? `Only patients booked for: ${f.appt_types.join(", ")}` : "All appointment types (tick to narrow it down)"}</p>
      <Input placeholder="Find an appointment type" value={find} onChange={(e) => setFind(e.target.value)} className="mt-1 h-8 text-xs" data-testid="custom-text-type-search" />
      <div className="mt-1 grid max-h-36 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2 sm:grid-cols-2" data-testid="custom-text-types">
        {shown.map((t) => (
          <label key={t} className="flex items-center gap-2 text-xs text-slate-700">
            <Checkbox checked={f.appt_types.includes(t)} onCheckedChange={() => toggle(t)} data-testid={`custom-text-type-${t.replace(/\W+/g, "-").toLowerCase()}`} />{t}
          </label>))}
        {!types.length && <p className="text-xs text-slate-400">No appointment types yet</p>}
      </div>
    </div>
  );
};

const Timing = ({ f, set }) => (
  <div className="flex flex-wrap items-center gap-2 text-sm">
    <span className="text-xs font-medium text-slate-700">Send</span>
    <Input type="number" min={f.timing === "after" ? 1 : 0} max={365} value={f.days} onChange={(e) => set({ days: Number(e.target.value) })} className="h-9 w-20" data-testid="custom-text-days" />
    <span className="text-xs text-slate-600">day(s)</span>
    <select className={sel} value={f.timing} onChange={(e) => set({ timing: e.target.value, status: "any", days: e.target.value === "after" ? Math.max(1, f.days) : f.days })} data-testid="custom-text-timing">
      <option value="before">before the appointment</option><option value="after">after the appointment</option>
    </select>
    {f.timing === "after" && <select className={sel} value={f.status} onChange={(e) => set({ status: e.target.value })} data-testid="custom-text-status">
      <option value="any">for everyone</option><option value="attended">only if they came in</option><option value="no_show">only if they didn't show</option>
    </select>}
    <p className="w-full text-[11px] text-slate-500">Goes out with the morning texts. 0 days before = the morning of the appointment.</p>
  </div>
);

export const CustomTextForm = ({ initial, phone = "713-828-1476", onDone }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(initial || BLANK);
  const set = (p) => setF((o) => ({ ...o, ...p }));
  const ref = useRef(null);
  const insert = (tag) => {
    const el = ref.current; const s = el?.selectionStart ?? f.template.length; const e = el?.selectionEnd ?? s; const ins = `{${tag}}`;
    set({ template: f.template.slice(0, s) + ins + f.template.slice(e) });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(s + ins.length, s + ins.length); });
  };
  const done = (msg) => { toast.success(msg); qc.invalidateQueries({ queryKey: ["automatic-texts"] }); onDone(); };
  const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
  const save = useMutation({ mutationFn: () => (initial ? api.put(`/custom-texts/${initial.id}`, f) : api.post("/custom-texts", f)), onSuccess: () => done(`${f.name || "Text"} saved`), onError: fail });
  const del = useMutation({ mutationFn: () => api.delete(`/custom-texts/${initial.id}`), onSuccess: () => done("Text deleted"), onError: fail });

  return (
    <div className="mt-2 space-y-3" data-testid="custom-text-form">
      <Input placeholder="Name, e.g. TRT 2-week check-in" value={f.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} data-testid="custom-text-name" />
      <Timing f={f} set={set} />
      <TypePicker f={f} set={set} />
      <Textarea ref={ref} rows={4} value={f.template} maxLength={480} onChange={(e) => set({ template: e.target.value })} data-testid="custom-text-input" />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-slate-500">Insert:</span>
        {TAGS.map((t) => <button key={t} type="button" onClick={() => insert(t)} data-testid={`custom-text-tag-${t}`}
          className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-700 hover:bg-slate-50">{`{${t}}`}</button>)}
        <span className="ml-auto text-[11px] text-slate-400">{[...f.template].length}/480</span>
      </div>
      <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-2" data-testid="custom-text-preview">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Live preview · what the patient sees</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-xs text-slate-800">{preview(f, phone) || <span className="italic text-slate-400">Start typing to see the text</span>}</p>
      </div>
      <p className="text-[11px] text-slate-500">Must include "Reply STOP to opt out." Patients who opted out never get it, and nothing sends while texts are paused.</p>
      <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="custom-text-enabled" />{f.enabled ? "On: sends with the morning texts" : "Off: saved but not sending"}</label>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-[#0B1F44] hover:bg-[#13306a]" disabled={save.isPending} onClick={() => save.mutate()} data-testid="custom-text-save">Save</Button>
        <Button size="sm" variant="outline" onClick={onDone} data-testid="custom-text-cancel">Cancel</Button>
        {initial && <Button size="sm" variant="ghost" className="text-red-600" disabled={del.isPending}
          onClick={() => window.confirm("Delete this text? It will stop sending.") && del.mutate()} data-testid="custom-text-delete">Delete</Button>}
      </div>
    </div>
  );
};
